import { Archive, CircleAlert, FolderOpen } from "lucide-react";
import * as React from "react";

import { Alert, Button } from "@/components/ui";
import { VideoProject } from "@/services/bedrock";
import { hasEditedVideo, hasRequiredEditedSrt } from "@/utils/editedDeliverables";

import { QaReturnedCallout } from "../QaReturnedCallout";

export function EditorProjectAlerts({
  videoProject,
  isCompleted,
  formDisabled,
  formErrorMessages,
  reopeningProject,
  onReopen,
  onReviewQa,
}: {
  videoProject: VideoProject;
  isCompleted: boolean;
  formDisabled: boolean;
  formErrorMessages?: string[];
  reopeningProject: boolean;
  onReopen: () => void;
  onReviewQa: () => void;
}) {
  return (
    <>
      {videoProject.archived && (
        <Alert variant="warning">
          <Archive />
          <Alert.Title>This project is archived</Alert.Title>
          <Alert.Description>
            It cannot be edited. Duplicate it if you need a new project from this one.
          </Alert.Description>
        </Alert>
      )}
      {isCompleted && !videoProject.archived && (
        <Alert>
          <FolderOpen />
          <Alert.Title>This project is completed</Alert.Title>
          <Alert.Description className="flex flex-col items-start gap-2">
            <p>Media, caption, hashtags, and publishing settings are read-only. Re-open the project to edit them.</p>
            <Button size="sm" variant="outline" onClick={onReopen} loading={reopeningProject}>
              Re-open project
            </Button>
          </Alert.Description>
        </Alert>
      )}
      {videoProject.status === VideoProject.Status.POST_FAILED && !videoProject.archived && (
        <Alert variant="warning">
          <CircleAlert />
          <Alert.Title>Posting failed</Alert.Title>
          <Alert.Description>The Player can retry from their library.</Alert.Description>
        </Alert>
      )}
      <QaReturnedCallout videoProject={videoProject} onReview={onReviewQa} />
      {!formDisabled &&
      videoProject.editedMedia?.some(hasEditedVideo) &&
      !hasRequiredEditedSrt(videoProject.editedMedia) ? (
        <Alert variant="warning">
          <CircleAlert />
          <Alert.Title>Caption SRT file is missing</Alert.Title>
          <Alert.Description>
            You can still submit this project. Add an SRT if the cut has on-screen captions so QA can review them.
          </Alert.Description>
        </Alert>
      ) : null}
      {formErrorMessages && formErrorMessages.length > 0 && (
        <Alert
          variant="destructive"
          className="border-red-200 bg-red-50 text-red-900 *:data-[slot=alert-description]:text-red-900"
        >
          <CircleAlert />
          <Alert.Title>Fix these issues</Alert.Title>
          <Alert.Description>
            {formErrorMessages.map((err: string, i: number) => (
              <p key={i}>{err}</p>
            ))}
          </Alert.Description>
        </Alert>
      )}
    </>
  );
}
